import { getBackendUrl } from "@Utils";
import { t } from "@i18n";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosWrapper from "@AxiosWrapper";
import { showToastNotification } from "../reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "../services/constants";
import { resolveFileAccess, type FileAccessResult } from "@Services/utils/fileViewPermission";
import type { RootState } from "@Reducers/index";
import type {
  GetFolderRolesListResponse,
  GetFolderRolesResponse,
  GetFolderRolesRequest,
  SetFolderRoleResponse,
  SpaceToggleResponse,
} from "@ServerTypes/folders/folders.schema";

export const getSpaceUserListForFolder = createAsyncThunk(
  "accessPermission/getSpaceUserListForFolder",
  async (_, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.get<GetFolderRolesListResponse>(
        `${getBackendUrl()}/folders/getFolderRolesList`,
      );
      if (responseData.data.status) {
        return (responseData.data as { data?: unknown }).data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

/**
 * The `data` parameter had no annotation, so `createAsyncThunk` inferred it as `void` — meaning the
 * only caller (`AccessContent`) was passing a payload the thunk's type said it could not accept, and
 * the body forwards it straight to `POST /folders/getFolderRoles`. Typed from the request schema.
 */
export const getFolderRoles = createAsyncThunk(
  "accessPermission/getFolderRoles",
  async (data: GetFolderRolesRequest, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<GetFolderRolesResponse>(
        `${getBackendUrl()}/folders/getFolderRoles`,
        data,
      );
      if (responseData.data.status) {
        return (responseData.data as any).data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

export const setDataForFolder = createAsyncThunk(
  "accessPermission/setDataForFolder",
  async (data: any, { dispatch, rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<SetFolderRoleResponse>(
        `${getBackendUrl()}/folders/setFolderRole`,
        data,
      );
      if (responseData.data.status) {
        dispatch(showToastNotification({ content: t("Permission changed successfully!") }));
        return responseData.data;
      }
      dispatch(
        showToastNotification({
          content: t("Insufficient permission access."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      dispatch(
        showToastNotification({
          content: t("Insufficient permission access."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

export const setAccessPermission = createAsyncThunk(
  "accessPermission/setAccessPermission",
  async (data: { fileVersionId: string; spaceId: string }, { rejectWithValue }) => {
    // The ONE isFile403 call. The whole result is stored in this slice so the
    // viewer's load gates and indicators read it from here instead of calling again.
    const access = await resolveFileAccess({ [data.fileVersionId]: { spaceId: data.spaceId } }, data.fileVersionId);
    // Tag the failure with the file it is about, so consumers can tell "this file's
    // check failed" (degrade to un-gated) from "still in flight" (wait).
    if (access.status !== true) return rejectWithValue({ ...access, fileVersionId: data.fileVersionId });
    return access;
  },
);

export const setSpaceToggle = createAsyncThunk(
  "accessPermission/setSpaceToggle",
  async (data: any, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<SpaceToggleResponse>(`${getBackendUrl()}/folders/spaceToggle`, data);
      if (responseData.data.status) {
        return responseData.data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(
        (error as { response?: { data?: unknown }; message?: string }).response?.data || (error as Error).message,
      );
    }
  },
);

const spaceUserDataForFolderSlice = createSlice({
  name: "spaceUserDataForFolder",
  initialState: [],
  reducers: {
    setSpaceUserListForFolder: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(getSpaceUserListForFolder.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const folderRolesSlice = createSlice({
  name: "folderRoles",
  initialState: [],
  reducers: {
    setFolderRoles: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(getFolderRoles.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const folderDataSlice = createSlice({
  name: "folderData",
  initialState: [],
  reducers: {
    setFolderData: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    builder.addCase(setDataForFolder.fulfilled, (_state: any, action: any) => action.payload);
  },
});

const accessPermissionSlice = createSlice({
  name: "accessPermission",
  initialState: {},
  reducers: {
    setAccessPermission: (_state, action) => action.payload,
  },
  extraReducers: (builder: any) => {
    // `requestedFor` records the file the newest dispatch is about, and settlements for
    // any other file are DROPPED. Without that, two checks in flight across a file
    // switch settle last-write-wins: a late answer for the previous file would land in
    // the store, selectFileAccessFor would then return undefined for the current file
    // forever (nothing re-dispatches), and every gated consumer would wait indefinitely.
    //
    // Note `pending` deliberately keeps the existing payload rather than clearing it:
    // selectFileAccessFor already scopes the gating fields by fileVersionId, while the
    // raw `data` / `canViewFile` readers (DesignViewer's permission map and view gate)
    // must not be blanked mid-switch.
    const isCurrent = (state: any, action: any) => action.meta?.arg?.fileVersionId === state?.requestedFor;
    builder
      .addCase(setAccessPermission.pending, (state: any, action: any) => ({
        ...state,
        requestedFor: action.meta?.arg?.fileVersionId,
      }))
      .addCase(setAccessPermission.rejected, (state: any, action: any) =>
        isCurrent(state, action) ? { ...(action.payload ?? {}), requestedFor: state.requestedFor } : state,
      )
      .addCase(setAccessPermission.fulfilled, (state: any, action: any) =>
        isCurrent(state, action) ? { ...action.payload, requestedFor: state.requestedFor } : state,
      );
  },
});

/**
 * The isFile403 result, but only when it is about `fileVersionId`. Returns undefined
 * while a check is in flight, after a failure, or when the store still holds the
 * previously opened file — so callers fall back to un-gated behaviour instead of
 * applying one file's permissions to another.
 *
 * `fileVersionId` is optional because callers read it from a prop or the viewer
 * context, where it isn't guaranteed yet; with no id there is nothing to match.
 */
export const selectFileAccessFor = (
  state: RootState,
  fileVersionId: string | undefined,
): FileAccessResult | undefined => {
  if (!fileVersionId) return undefined;
  const access = state.getAccessPermission as FileAccessResult;
  return access?.fileVersionId && access.fileVersionId === fileVersionId ? access : undefined;
};

const spaceToggleSlice = createSlice({
  name: "spaceToggle",
  initialState: {} as any,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setSpaceToggle.fulfilled, (_state, action) => action.payload);
  },
});

export const spaceUserDataForFolder = spaceUserDataForFolderSlice.reducer;
export const setFolderRoles = folderRolesSlice.reducer;
export const setFolderData = folderDataSlice.reducer;
export const getAccessPermission = accessPermissionSlice.reducer;
export const getSpaceToggle = spaceToggleSlice.reducer;
